import { Keyboard, Platform, View, useWindowDimensions } from 'react-native'
import { StyleSheet } from 'react-native-unistyles';
import { useRouter, useIsFocused } from 'expo-router';
import { Pressable } from 'react-native';
import ModalHeader from './ModalHeader';
import ReachToast from './ReachToast';
import Animated, { FadeIn, FadeOut, SlideInRight, SlideOutRight } from 'react-native-reanimated';
import usePlatform from '@/utils/hooks/usePlatform';
import { useCallback, useEffect, useRef, useState } from 'react';
import useDropdownStore from '@/data/store/useDropdownStore';
import useModalPortalStore, { ModalPortalContext } from '@/data/store/useModalPortalStore';
import { ConfirmationModal } from '@/utils/showAlert';
import Toast from 'react-native-toast-message';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { TooltipOffsetContext } from './Tooltip';
import useGuideBlocker from '@/utils/hooks/useGuideBlocker';

const ModalWrapper = ({children, title, subtitle, helpText, cancelText, confirmText, confirmDisabled, confirmTestID, loading, onConfirm, onCancel, onBeforeClose, noPadding, width = 500}) => {
    const router = useRouter();
    const { isWeb } = usePlatform();
    const insets = useSafeAreaInsets();
    const { width: screenWidth } = useWindowDimensions();
    const [isClosing, setIsClosing] = useState(false);
    const isDropdownOpen = useDropdownStore((state) => state.open);
    const isFocused = useIsFocused();
    useGuideBlocker(isFocused && !isClosing);
    const modalNodeRef = useRef(null);
    const drawerWidth = Math.min(width, screenWidth);

    // Register this modal's DOM node as a Radix Portal container for DropdownMenu
    // content on web. Unregisters synchronously on unmount (ref null + effect
    // cleanup), before Reanimated's exit animation lingers the node — see
    // useModalPortalStore for why the old querySelectorAll lookup broke dropdowns.
    const registerModalNode = useCallback((node) => {
        if (!isWeb) return;
        if (node) {
            modalNodeRef.current = node;
            useModalPortalStore.getState().register(node);
        } else {
            const prev = modalNodeRef.current;
            if (prev) useModalPortalStore.getState().unregister(prev);
            modalNodeRef.current = null;
        }
    }, [isWeb]);

    useEffect(() => () => {
        if (isWeb && modalNodeRef.current) useModalPortalStore.getState().unregister(modalNodeRef.current);
    }, [isWeb]);

    // Portal host for this drawer's subtree (DropdownMenu/Zeego popovers on
    // web). Provided via ModalPortalContext so popovers in THIS drawer portal
    // here — above its backdrop — rather than into whatever modal happens to
    // be top-of-stack when multiple drawers/sheets are open. The outer
    // container is used (not the inner panel): it never carries a transform,
    // so position:fixed popovers keep viewport coordinates and can't be
    // clipped by the panel's overflow:hidden.
    const [portalHost, setPortalHost] = useState(null);
    const registerPortalHost = useCallback((node) => {
        if (isWeb) setPortalHost(node);
    }, [isWeb]);

    useEffect(() => {
        const listener = (e) => {
            if(!isFocused) return;
            if(e.key == "Escape" && !isDropdownOpen){
                // close() runs onCancel + back once — calling onCancel here too
                // would double-fire the cancel callback.
                close();
            }
            else if(e.key == "Enter" && (e.metaKey || e.ctrlKey) && !isDropdownOpen){
                e.preventDefault();
                if(onConfirm) onConfirm();
            }
            else if(e.metaKey || e.ctrlKey){
                document.activeElement.blur();
            }
        }

        if(isWeb) window.addEventListener('keydown', listener, true);

        return () => {
            if(isWeb) window.removeEventListener('keydown', listener, true);
        }

    }, [isDropdownOpen, onConfirm, onCancel, onBeforeClose, isFocused])

    useEffect(() => {
        // Hide any existing toast when modal opens to prevent it from getting stuck
        Toast.hide();
    }, [])

    const close = () => {
        // Draft screens can defer dismissal until their discard prompt resolves.
        if (onBeforeClose?.() === false) return;
        if(isWeb){
            setIsClosing(true);
            setTimeout(() => {
                Keyboard.dismiss();
                if(onCancel) onCancel();
                router.back();
                setIsClosing(false);
            }, 300);
        }
        else{
            Keyboard.dismiss();
            if(onCancel) onCancel();
            router.back();
        }
    }

    return (isClosing ? null :
       <Animated.View ref={isWeb ? registerPortalHost : undefined} entering={isWeb ? FadeIn.duration(300) : undefined} exiting={isWeb ? FadeOut.duration(300) : undefined} style={styles.outer(isWeb)}>
            <Pressable style={StyleSheet.absoluteFill} onPress={close} />
            <Animated.View
                ref={isWeb ? registerModalNode : undefined}
                entering={isWeb ? SlideInRight.duration(300) : undefined}
                exiting={isWeb ? SlideOutRight.duration(300) : undefined}
                style={[styles.inner(isWeb), isWeb && {width: drawerWidth}]}
                aria-modal="true"
                role="dialog"
            >
                <ModalPortalContext.Provider value={portalHost}>
                    <ModalHeader
                        helpText={helpText}
                        cancelText={cancelText}
                        forSheet
                        onCancel={close}
                        goBackOnCancel={false}
                        loading={loading}
                        confirmText={confirmText}
                        confirmDisabled={confirmDisabled}
                        confirmTestID={confirmTestID}
                        onConfirm={onConfirm ? () => onConfirm() : undefined}
                        title={title}
                        subtitle={subtitle}
                    />

                    <View style={styles.content(noPadding)}>
                        <TooltipOffsetContext.Provider value={insets.top}>
                            {children}
                        </TooltipOffsetContext.Provider>
                    </View>
                </ModalPortalContext.Provider>
            </Animated.View>

            {isWeb && <ConfirmationModal />}
            {!isWeb && <ReachToast />}
        </Animated.View>
    )
}


const styles = StyleSheet.create(({colors}, {themeName, screen, isPortrait, isLandscape}) => ({
    outer: (isWeb) => ({
       flex:1,
       width:"100%",
       flexDirection:isWeb ? "row" : "column",
       alignItems:isWeb ? "stretch" : "center",
       justifyContent:isWeb ? "flex-end" : (isLandscape ? "center" : undefined),
       backgroundColor:themeName == "dark" ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.3)",
       cursor: "pointer"
    }),
    inner: (isWeb) => ({
        flex: isPortrait && !isWeb ? 1 : undefined,
        width:isWeb ? 500 : "100%",
        ...(isWeb ? { maxWidth: screen.width } : {}),
        minWidth:isLandscape && !isWeb ? 500 : 0,
        ...(isWeb ? { height: "100%" } : {}),
        maxHeight:isLandscape && !isWeb ? screen.height * .9 : undefined,
        overflow:"hidden",
        padding:isWeb ? 20 : (isLandscape ? 20 : 0),
        backgroundColor:colors.background,
        borderRadius:isWeb ? 0 : 8,
        boxShadow:isWeb ? "-4px 0px 16px 0px rgba(0,0,0,0.2)" : "0px 0px 8px 0px rgba(0,0,0,0.3)",
        cursor: "default"
    }),
    content: (noPadding) => ({
        flex: isPortrait || Platform.OS === 'web' ? 1 : undefined,
        height: isLandscape && Platform.OS !== 'web' ? (screen.height * .9) - 90 : undefined,
        maxHeight:isLandscape ? (screen.height * .9) - 90 : undefined,
        overflow:"auto",
        paddingHorizontal:noPadding && isPortrait ? 0 : 10,
        cursor: "default"
    })
}))

export default ModalWrapper